import { collection, getDocs, query, orderBy as firestoreOrderBy, limit as firestoreLimit, where, addDoc, doc, deleteDoc, getDoc, updateDoc, arrayUnion, arrayRemove, increment, startAfter, onSnapshot, Timestamp } from 'firebase/firestore';
import { db } from '../../../firebase';
import { PostRepository } from '../../../domain/posts/repositories/PostRepository';
import { toDate } from '../../../shared/utils/date/DateExtensions';
import { logger } from '../../../shared/utils/logger';
import { CACHE_CONFIG, PAGINATION_CONFIG } from '../../../core/config/cache.config';
import { analytics } from '../../../firebase';
import { logEvent } from 'firebase/analytics';
import { geohashForLocation, geohashQueryBounds, distanceBetween } from 'geofire-common';
import { GEO } from '../../../core/constants/geo';
import { mapFirestorePostToUIPost } from '../../../shared/utils/posts/mapFirestorePostToUIPost';

// eslint-disable-next-line no-unused-vars
const logAnalyticsEvent = (eventName, params) => {
  if (process.env.NODE_ENV === 'production' && analytics) {
    logEvent(analytics, eventName, params);
  } else {
    logger.debug('Analytics event (dev):', { eventName, params });
  }
};

export class FirestorePostRepository extends PostRepository {
  constructor() {
    super();
    this.collection = collection(db, 'posts');
    this.cache = {
      posts: new Map(),
      queries: new Map(),
      ttl: CACHE_CONFIG.TTL
    };
    this.totalQueries = 0;
  }

  getCacheKey(params) {
    return JSON.stringify({
      limit: params.limit,
      dateFilter: params.dateFilter,
      searchQuery: params.searchQuery,
      orderBy: params.orderBy
    });
  }

  getNearbyPostsCacheKey(params) {
    return JSON.stringify({
      center: params.center,
      radiusInKm: params.radiusInKm,
      limit: params.limit
    });
  }

  getFromCache(cacheKey) {
    const cached = this.cache.queries.get(cacheKey);
    if (!cached) return null;

    const now = Date.now();
    if (now - cached.timestamp > this.cache.ttl) {
      this.cache.queries.delete(cacheKey);
      return null;
    }

    return cached.data;
  }

  setInCache(cacheKey, data) {
    this.cache.queries.set(cacheKey, {
      data,
      timestamp: Date.now()
    });

    // Mettre en cache les posts individuels aussi
    data.posts.forEach(post => {
      this.cache.posts.set(post.id, {
        data: post,
        timestamp: Date.now()
      });
    });
  }

  checkCacheSize() {
    if (this.cache.queries.size > CACHE_CONFIG.MAX_ITEMS * CACHE_CONFIG.PURGE_THRESHOLD) {
      this.purgeOldCache();
    }
  }

  purgeOldCache() {
    const now = Date.now();
    let purgedCount = 0;

    // Purger les vieilles entrées
    for (const [key, value] of this.cache.queries.entries()) {
      if (now - value.timestamp > this.cache.ttl) {
        this.cache.queries.delete(key);
        purgedCount++;
      }
    }

    // Si toujours trop plein, purger les plus vieilles entrées
    if (this.cache.queries.size > CACHE_CONFIG.MAX_ITEMS) {
      const entries = Array.from(this.cache.queries.entries())
        .sort((a, b) => a[1].timestamp - b[1].timestamp);
      
      const toDelete = entries.slice(0, entries.length - CACHE_CONFIG.MAX_ITEMS);
      toDelete.forEach(([key]) => {
        this.cache.queries.delete(key);
        purgedCount++;
      });
    }

    logger.info('Cache purged:', { purgedCount });
  }

  async getPosts({ 
    limit = 10, 
    lastVisible = null,
    excludeUserId = null,
    searchQuery = '',
    dateFilter = 'all',
    orderBy = { field: 'activity.date', direction: 'asc' },
    useCache = true,
    refresh = false
  } = {}) {
    try {
      this.totalQueries++;
      
      console.log('DEBUG - FirestorePostRepository.getPosts started:', {
        limit, 
        dateFilter,
        orderByField: orderBy.field,
        orderByDirection: orderBy.direction,
        hasLastVisible: !!lastVisible,
        excludeUserId
      });
      
      if (this.totalQueries > PAGINATION_CONFIG.ITEMS_PER_PAGE * PAGINATION_CONFIG.MAX_PAGES) {
        logAnalyticsEvent('pagination_limit_reached', {
          total_queries: this.totalQueries
        });
        return {
          posts: [],
          lastVisible: null,
          hasMore: false
        };
      }

      if (refresh) {
        this.clearCache();
      }

      if (useCache && !refresh && !lastVisible) {
        const cacheKey = this.getCacheKey({ limit: limit, dateFilter, searchQuery, orderBy });
        this.checkCacheSize();

        const cached = this.getFromCache(cacheKey);
        if (cached) {
          logger.debug('📦 Utilisation du cache pour les posts');
          return cached;
        }
      }

      const queryLimit = limit + 1;
      const constraints = [];

      // Filtrer les activités futures uniquement
      const now = new Date();
      console.log('DEBUG - Filtrage par date:', { 
        currentDate: now.toISOString(),
        filterCondition: 'Affichage uniquement des activités futures'
      });
      
      // Réactivation du filtre pour n'afficher que les activités futures
      constraints.push(where('activity.date', '>', now));

      // Tri chronologique (du plus proche au plus lointain)
      constraints.push(firestoreOrderBy('activity.date', 'asc'));

      if (lastVisible) {
        constraints.push(startAfter(lastVisible));
      }

      constraints.push(firestoreLimit(queryLimit));

      logger.debug('🔄 Chargement des posts depuis Firestore...');
      let q = query(this.collection, ...constraints);
      const snapshot = await getDocs(q);
      const docs = snapshot.docs;
      
      console.log('DEBUG - Résultat de la requête Firestore:', {
        documentsCount: docs.length,
        empty: snapshot.empty
      });

      let postsData = docs.slice(0, limit).map(doc => {
        const data = doc.data();
        return mapFirestorePostToUIPost(data, doc.id);
      });

      if (excludeUserId) {
        postsData = postsData.filter(post => post.author.uid !== excludeUserId);
      }

      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase();
        postsData = postsData.filter(post => 
          post.content?.toLowerCase().includes(searchLower) ||
          post.activity?.type?.toLowerCase().includes(searchLower) ||
          post.activity?.subtype?.toLowerCase().includes(searchLower) ||
          post.activity?.location?.toLowerCase().includes(searchLower)
        );
      }

      const hasMore = docs.length > limit;

      const lastVisibleDoc = hasMore ? docs[limit - 1] : null;

      const result = {
        posts: postsData,
        lastVisible: lastVisibleDoc,
        hasMore
      };

      if (useCache && !refresh && !lastVisible) {
        const cacheKey = this.getCacheKey({ limit: limit, dateFilter, searchQuery, orderBy });
        this.setInCache(cacheKey, result);
      }

      logAnalyticsEvent('firestore_query', {
        query_type: lastVisible ? 'pagination' : 'initial',
        filter_type: dateFilter,
        has_search: !!searchQuery,
        results_count: result.posts.length
      });

      return result;
    } catch (error) {
      logAnalyticsEvent('query_error', {
        error_message: error.message
      });
      logger.error('Error in getPosts:', error);
      throw error;
    }
  }

  async create(post) {
    try {
      const { participants, ...postData } = post;
      
      // Calculer le geohash si l'activité a une localisation
      let geohash = null;
      let lat = null;
      let lng = null;
      
      if (post.activity?.location?.location?.lat && post.activity?.location?.location?.lng) {
        lat = post.activity.location.location.lat;
        lng = post.activity.location.location.lng;
        geohash = geohashForLocation([lat, lng]);
      }
      
      const postToSave = {
        ...postData,
        createdAt: Timestamp.now(),
        activity: post.activity ? {
          ...post.activity,
          date: post.activity.date ? Timestamp.fromDate(new Date(post.activity.date)) : null,
          participants: [],
          currentParticipants: 0,
          maxParticipants: post.activity.maxParticipants || 8
        } : null,
        author: {
          uid: post.author.uid,
          name: post.author.name,
          photoURL: post.author.photoURL
        },
        saves: [],
        shares: []
      };
      
      // Ajouter les informations géographiques si disponibles
      if (geohash) {
        postToSave.geohash = geohash;
        postToSave.lat = lat;
        postToSave.lng = lng;
      }
      
      const docRef = await addDoc(this.collection, postToSave);
      return { id: docRef.id, ...postToSave };
    } catch (error) {
      throw error;
    }
  }

  /**
   * @param {string} postId
   * @param {string} userId
   * @returns {Promise<boolean>}
   */
  async deletePost(postId, userId) {
    try {
      const postRef = doc(this.collection, postId);
      
      const postSnap = await getDoc(postRef);

      if (!postSnap.exists()) {
        throw new Error('Post not found');
      }

      const postData = postSnap.data();
      if (postData.author.uid !== userId) {
        throw new Error('Unauthorized: You can only delete your own posts');
      }

      await deleteDoc(postRef);
      return true;
    } catch (error) {
      throw new Error(`Failed to delete post: ${error.message}`);
    }
  }

  async joinActivity(postId, userId, participantDetails) {
    const postRef = doc(this.collection, postId);
    
    logger.debug('Adding participant with details:', participantDetails);
    
    await updateDoc(postRef, {
      'activity.participants': arrayUnion(userId),
      'activity.currentParticipants': increment(1),
      'activity.participantsDetails': arrayUnion(participantDetails)
    });
    
    // Invalider le cache pour ce post
    this.cache.posts.delete(postId);
    this.cache.queries.clear();
  }

  async leaveActivity(postId, userId, participantDetails) {
    const postRef = doc(this.collection, postId);
    
    // Récupérer d'abord le document pour vérifier les données actuelles
    const postDoc = await getDoc(postRef);
    if (!postDoc.exists()) {
      return { success: false, error: 'Post not found' };
    }
    
    const postData = postDoc.data();
    const currentCount = postData.activity?.currentParticipants || 0;
    
    const updateData = {
      'activity.participants': arrayRemove(userId),
      // Ensure currentParticipants never goes below 0
      'activity.currentParticipants': Math.max(0, currentCount - 1)
    };
    
    // N'ajouter participantsDetails à la mise à jour que s'il est défini
    if (participantDetails) {
      updateData['activity.participantsDetails'] = arrayRemove(participantDetails);
    }
    
    try {
      await updateDoc(postRef, updateData);
      
      // Invalider le cache pour ce post
      this.cache.posts.delete(postId);
      this.cache.queries.clear();
      
      return { success: true };
    } catch (error) {
      logger.error('Error removing participant:', error);
      return { success: false, error: error.message };
    }
  }

  async toggleParticipation(postId, { currentParticipants, currentDetails, participantData }) {
    try {
      const postRef = doc(db, 'posts', postId);
      const userId = participantData.uid;
      const isParticipating = currentParticipants.includes(userId);
      
      if (isParticipating) {
        await updateDoc(postRef, {
          'activity.participants': arrayRemove(userId),
          'activity.participantsDetails': arrayRemove(participantData),
          'activity.currentParticipants': increment(-1)
        });
      } else {
        await updateDoc(postRef, {
          'activity.participants': arrayUnion(userId),
          'activity.participantsDetails': arrayUnion(participantData),
          'activity.currentParticipants': increment(1)
        });
      }

      // Invalider le cache pour ce post
      this.cache.posts.delete(postId);
      this.cache.queries.clear();

      return { 
        success: true,
        data: {
          participants: isParticipating 
            ? currentParticipants.filter(id => id !== userId)
            : [...currentParticipants, userId],
          participantsDetails: isParticipating
            ? currentDetails.filter(p => p.uid !== userId)
            : [...currentDetails, participantData]
        }
      };
    } catch (error) {
      logger.error('Error toggling participation:', error);
      return {
        success: false,
        error: error.message || 'Failed to toggle participation'
      };
    }
  }

  clearCache() {
    this.cache.queries.clear();
    this.cache.posts.clear();
  }

  /**
   * Récupère un post par son ID
   * @param {string} postId - L'ID du post à récupérer
   * @returns {Promise<Object>} - Le post récupéré ou null si non trouvé
   */
  async getPostById(postId) {
    try {
      // Vérifier si le post est en cache
      const cachedPost = this.cache.posts.get(postId);
      if (cachedPost && Date.now() - cachedPost.timestamp < this.cache.ttl) {
        logger.debug('📦 Utilisation du cache pour le post:', postId);
        return cachedPost.data;
      }

      // Récupérer le post depuis Firestore
      const postRef = doc(this.collection, postId);
      const docSnapshot = await getDoc(postRef);
      
      if (!docSnapshot.exists()) {
        return null;
      }
      
      const data = docSnapshot.data();
      
      // Formater les données du post
      const formattedPost = mapFirestorePostToUIPost(data, docSnapshot.id);
      
      // Mettre en cache
      this.cache.posts.set(postId, {
        data: formattedPost,
        timestamp: Date.now()
      });
      
      return formattedPost;
    } catch (error) {
      logger.error('Error getting post by ID:', error);
      throw error;
    }
  }

  /**
   * S'abonne aux mises à jour en temps réel d'un post spécifique
   * @param {Object} params - Paramètres de l'abonnement
   * @param {string} params.postId - ID du post à écouter
   * @param {Function} params.onUpdate - Callback appelé quand le post est mis à jour
   * @param {Function} params.onError - Callback appelé en cas d'erreur
   * @returns {Function} - Fonction pour désabonner
   */
  async subscribeToPost({ postId, onUpdate, onError }) {
    try {
      if (!postId) {
        throw new Error('postId is required');
      }

      const postRef = doc(this.collection, postId);
      
      // Établir l'abonnement temps réel
      const unsubscribe = onSnapshot(
        postRef,
        (docSnapshot) => {
          if (docSnapshot.exists()) {
            const data = docSnapshot.data();
            const post = mapFirestorePostToUIPost(data, docSnapshot.id);
            
            // Mettre à jour le cache
            this.cache.posts.set(postId, {
              data: post,
              timestamp: Date.now()
            });
            
            onUpdate(post);
            // logger.debug('🔄 Post mis à jour en temps réel:', postId);
          } else {
            onError(new Error('Post non trouvé'));
          }
        },
        (err) => {
          logger.error('Error in subscription to post:', err);
          onError(err);
        }
      );

      return unsubscribe;
    } catch (error) {
      logger.error('Error setting up post subscription:', error);
      onError(error);
      return () => {}; // Return empty unsubscribe function
    }
  }

  /**
   * Récupère les posts à proximité d'un point géographique
   * @param {Object} params - Paramètres de recherche
   * @param {Object} params.center - Coordonnées du centre {latitude, longitude}
   * @param {number} params.radiusInKm - Rayon de recherche en kilomètres
   * @param {number} [params.limit=20] - Nombre maximum de résultats
   * @param {string} [params.excludeUserId] - ID de l'utilisateur à exclure
   * @param {boolean} [params.refresh=false] - Force le rafraîchissement des données
   * @returns {Promise<Object>} - Résultats de la recherche
   */
  async getNearbyPosts({
    center,
    radiusInKm = GEO.DEFAULT_RADIUS_KM,
    limit = 20,
    excludeUserId = null,
    refresh = false
  } = {}) {
    try {
      this.totalQueries++;
      
      if (this.totalQueries > PAGINATION_CONFIG.ITEMS_PER_PAGE * PAGINATION_CONFIG.MAX_PAGES) {
        logAnalyticsEvent('pagination_limit_reached', {
          total_queries: this.totalQueries
        });
        return {
          posts: [],
          hasMore: false
        };
      }

      // Utiliser le cache si disponible et si refresh n'est pas true
      if (!refresh) {
        const cacheKey = this.getNearbyPostsCacheKey({ center, radiusInKm, limit });
        this.checkCacheSize();

        const cached = this.getFromCache(cacheKey);
        if (cached) {
          logger.debug('📦 Utilisation du cache pour les posts géolocalisés');
          return cached;
        }
      }

      // Convertir les coordonnées au format attendu par geofire
      const centerPoint = [center.latitude, center.longitude];
      const radiusInM = radiusInKm * 1000;

      // Calculer les limites de recherche géospatiale
      const bounds = geohashQueryBounds(centerPoint, radiusInM);
      const promises = [];

      logger.debug('🌍 Recherche de posts dans un rayon de', radiusInKm, 'km');
      
      // Créer une requête Firestore pour chaque limite géospatiale
      for (const b of bounds) {
        const q = query(
          this.collection,
          firestoreOrderBy('geohash'),
          where('geohash', '>=', b[0]),
          where('geohash', '<=', b[1])
        );
        promises.push(getDocs(q));
      }

      // Attendre que toutes les requêtes soient terminées
      const snapshots = await Promise.all(promises);
      
      // Collecter tous les résultats et filtrer les faux positifs
      const matchingDocs = [];
      
      // Date actuelle pour filtrer les activités passées
      const now = new Date();
      
      for (const snapshot of snapshots) {
        for (const doc of snapshot.docs) {
          const data = doc.data();
          
          // Vérifier d'abord si l'activité est future (date > now)
          if (data.activity?.date && toDate(data.activity.date) <= now) {
            continue; // Ignorer les activités passées
          }
          
          // Vérifier si le document a une localisation
          if (!data.lat || !data.lng) {
            // Essayer de récupérer depuis la structure de location
            if (data.activity?.location?.location?.lat && data.activity?.location?.location?.lng) {
              data.lat = data.activity.location.location.lat;
              data.lng = data.activity.location.location.lng;
            } else {
              continue; // Pas de coordonnées, ignorer ce document
            }
          }
          
          // Calculer la distance exacte
          const distanceInKm = distanceBetween(
            [data.lat, data.lng],
            centerPoint
          );
          
          // Filtrer les points qui sont vraiment dans le rayon (éliminer les faux positifs)
          if (distanceInKm <= radiusInKm) {
            // Créer l'objet post avec la distance
            const post = mapFirestorePostToUIPost(data, doc.id);
            post.distance = distanceInKm; // Ajouter la distance pour permettre le tri
            
            // Exclure les posts de l'utilisateur si demandé
            if (excludeUserId && post.author.uid === excludeUserId) {
              continue;
            }
            
            matchingDocs.push(post);
          }
        }
      }

      // Trier par distance
      matchingDocs.sort((a, b) => a.distance - b.distance);
      
      // Limiter le nombre de résultats
      const limitedDocs = matchingDocs.slice(0, limit);
      
      const result = {
        posts: limitedDocs,
        hasMore: matchingDocs.length > limit
      };

      // Mettre en cache les résultats
      if (!refresh) {
        const cacheKey = this.getNearbyPostsCacheKey({ center, radiusInKm, limit });
        this.setInCache(cacheKey, result);
      }

      logAnalyticsEvent('geo_firestore_query', {
        radius_km: radiusInKm,
        results_count: limitedDocs.length,
        total_results: matchingDocs.length
      });

      return result;
    } catch (error) {
      logAnalyticsEvent('geo_query_error', {
        error_message: error.message
      });
      logger.error('Error in getNearbyPosts:', error);
      throw error;
    }
  }

  /**
   * S'abonne aux mises à jour en temps réel des posts à proximité
   * @param {Object} params - Paramètres de l'abonnement
   * @param {Object} params.center - Coordonnées du centre {latitude, longitude}
   * @param {number} params.radiusInKm - Rayon de recherche en kilomètres
   * @param {number} params.limit - Nombre maximum de résultats
   * @param {string} params.excludeUserId - ID de l'utilisateur à exclure
   * @param {Function} params.onUpdate - Callback appelé quand les posts sont mis à jour
   * @param {Function} params.onError - Callback appelé en cas d'erreur
   * @returns {Function} - Fonction pour désabonner
   */
  async subscribeToNearbyPosts({ center, radiusInKm = GEO.DEFAULT_RADIUS_KM, limit = 20, excludeUserId, onUpdate, onError }) {
    try {
      if (!center || !center.latitude || !center.longitude) {
        throw new Error('center coordinates are required');
      }

      // Convertir les coordonnées au format attendu par geofire
      const centerPoint = [center.latitude, center.longitude];
      const radiusInM = radiusInKm * 1000;

      // Calculer les limites de recherche géospatiale
      const bounds = geohashQueryBounds(centerPoint, radiusInM);
      const unsubscribeFunctions = [];
      
      // Map pour stocker tous les posts de toutes les requêtes
      const allPostsMap = new Map();
      
      // Variables pour le debouncing
      let debounceTimer = null;
      let lastResultHash = null;

      logger.debug('🌍 Abonnement temps réel aux posts dans un rayon de', radiusInKm, 'km');
      
      // Fonction pour agréger et envoyer les résultats avec debouncing
      const aggregateAndSendResults = () => {
        // Annuler le timer précédent
        if (debounceTimer) {
          clearTimeout(debounceTimer);
        }
        
        // Programmer l'envoi des résultats avec un délai
        debounceTimer = setTimeout(() => {
          const allPosts = Array.from(allPostsMap.values());
          
          // Filtrer par distance et exclure l'utilisateur si nécessaire
          const filteredPosts = allPosts.filter(post => {
            // Exclure les posts de l'utilisateur si demandé
            if (excludeUserId && post.author.uid === excludeUserId) {
              return false;
            }
            
            // Vérifier la distance
            if (post.distance > radiusInKm) {
              return false;
            }
            
            // Filtrer les activités passées avec une date actuelle fraîche
            const now = new Date();
            if (post.activity?.date && toDate(post.activity.date) <= now) {
              return false;
            }
            
            return true;
          });
          
          // CORRECTION: Trier par date d'activité (chronologique) au lieu de distance
          const sortedPosts = filteredPosts
            .sort((a, b) => {
              // Tri chronologique : activités les plus proches dans le temps en premier
              if (a.activity?.date && b.activity?.date) {
                return toDate(a.activity.date) - toDate(b.activity.date);
              }
              // Fallback sur la distance si pas de date
              return a.distance - b.distance;
            })
            .slice(0, limit);
          
          // Créer un hash des résultats pour éviter les mises à jour identiques
          const resultHash = sortedPosts.map(p => `${p.id}-${p.activity?.currentParticipants || 0}-${toDate(p.activity?.date)?.getTime() || 0}`).join(',');
          
          // N'envoyer la mise à jour que si les résultats ont changé
          if (resultHash !== lastResultHash) {
            lastResultHash = resultHash;
            onUpdate({
              posts: sortedPosts,
              hasMore: filteredPosts.length > limit
            });
            logger.debug('📡 Mise à jour envoyée:', { postsCount: sortedPosts.length });
          }
        }, 100); // Debounce de 100ms
      };
      
      // Créer une requête Firestore pour chaque limite géospatiale
      for (const b of bounds) {
        const q = query(
          this.collection,
          firestoreOrderBy('geohash'),
          where('geohash', '>=', b[0]),
          where('geohash', '<=', b[1])
        );

        const unsubscribe = onSnapshot(
          q,
          (snapshot) => {
            let hasChanges = false;
            
            // Traiter les changements pour cette requête spécifique
            snapshot.docChanges().forEach(change => {
              const doc = change.doc;
              const data = doc.data();
              
              if (change.type === 'removed') {
                // Supprimer le post de la map
                if (allPostsMap.has(doc.id)) {
                  allPostsMap.delete(doc.id);
                  hasChanges = true;
                  // logger.debug('🗑️ Post supprimé:', doc.id);
                }
              } else {
                // Vérifier si le document a une localisation
                if (!data.lat || !data.lng) {
                  // Essayer de récupérer depuis la structure de location
                  if (data.activity?.location?.location?.lat && data.activity?.location?.location?.lng) {
                    data.lat = data.activity.location.location.lat;
                    data.lng = data.activity.location.location.lng;
                  } else {
                    // Si le post était dans la map mais n'a plus de coordonnées, le supprimer
                    if (allPostsMap.has(doc.id)) {
                      allPostsMap.delete(doc.id);
                      hasChanges = true;
                      // logger.debug('🗑️ Post supprimé (pas de coordonnées):', doc.id);
                    }
                    return; // Pas de coordonnées, ignorer ce document
                  }
                }
                
                // Calculer la distance exacte
                const distanceInKm = distanceBetween(
                  [data.lat, data.lng],
                  centerPoint
                );
                
                // Créer l'objet post avec la distance
                const post = mapFirestorePostToUIPost(data, doc.id);
                post.distance = distanceInKm;
                
                // CORRECTION: Simplifier la détection des changements
                const existingPost = allPostsMap.get(doc.id);
                const shouldUpdate = !existingPost || 
                  change.type === 'modified' ||
                  existingPost.activity?.currentParticipants !== post.activity?.currentParticipants ||
                  JSON.stringify(existingPost.activity?.participants || []) !== JSON.stringify(post.activity?.participants || []);
                
                if (shouldUpdate) {
                  // Ajouter ou mettre à jour le post dans la map
                  allPostsMap.set(doc.id, post);
                  hasChanges = true;
                  // logger.debug(`${change.type === 'added' ? '➕' : '🔄'} Post ${change.type}:`, doc.id);
                }
              }
            });
            
            // N'agréger et envoyer les résultats que s'il y a eu des changements
            if (hasChanges) {
              aggregateAndSendResults();
            }
          },
          (err) => {
            logger.error('Error in subscription to nearby posts:', err);
            onError(err);
          }
        );

        unsubscribeFunctions.push(unsubscribe);
      }

      // Retourner une fonction qui désabonne toutes les requêtes
      return () => {
        if (debounceTimer) {
          clearTimeout(debounceTimer);
        }
        unsubscribeFunctions.forEach(unsubscribe => unsubscribe());
        // logger.debug('🔌 Abonnement aux posts à proximité fermé');
      };
    } catch (error) {
      logger.error('Error setting up nearby posts subscription:', error);
      onError(error);
      return () => {}; // Return empty unsubscribe function
    }
  }
} 